/* /ess/docs — own documents with expiry status (prototype pgEssDocs). Personal documents
 * (passport, national ID, driving licence) show the step-by-step renewal guide; employer-handled
 * ones (Iqama, work permit, insurance, exit re-entry) let the employee ask HR to renew. */
import { Fragment, useState } from "react";
import { useEmployee, useToday } from "../../data/hooks";
import type { EmpDocument } from "../../data/types";
import { daysTo } from "../../domain/dates";
import { useI18n } from "../../i18n";
import { Alert, Button, Card, DualDate, EmptyState, ExpiryChip, Icon, Loaded, PageHeader, useToast } from "../../ui";
import RenewalGuide from "../core/components/RenewalGuide";
import { NoLink } from "./EssHome";
import { PERSONAL_DOCS, useEmployeeId } from "./essLib";

export const isPersonalDoc = (kind: string) => PERSONAL_DOCS.includes(kind);
const iconFor = (k: string) => (k === "Iqama" || k === "National ID" ? "id-card" : k === "Medical insurance" ? "shield" : /Exit re-entry/.test(k) ? "plane" : k === "Passport" ? "globe" : "file");

function Body({ empId }: { empId: number }) {
  const { t, L } = useI18n();
  const toast = useToast();
  const today = useToday();
  const q = useEmployee(empId);
  const [guide, setGuide] = useState<string | null>(null);
  const [requested, setRequested] = useState<Record<string, boolean>>({});
  const request = (d: EmpDocument) => {
    setRequested({ ...requested, [d.kind]: true });
    toast(L("Renewal request sent to HR · " + d.kind, "تم إرسال طلب التجديد إلى الموارد البشرية · " + t(d.kind)));
  };
  return (
    <div className="ess" data-testid="ess-docs">
      <PageHeader title={t("My documents")} sub={L("Expiry reminders at 90, 60 and 30 days", "تذكير بالانتهاء قبل 90 و60 و30 يومًا")} />
      <Loaded q={q}>{(e) => {
        const docs = e.documents;
        const soon = docs.filter((d) => daysTo(today, d.expiry) <= 30);
        return (
          <>
            {soon.length > 0 && <div className="mb"><Alert tone="warn" testId="docs-soon" title={L(`${soon.length} document${soon.length > 1 ? "s" : ""} need attention within 30 days`, `${soon.length} وثائق تحتاج متابعة خلال 30 يومًا`)} desc={L("Personal documents are renewed by you; HR renews your Iqama, work permit and insurance.", "تجدد وثائقك الشخصية بنفسك، وتجدد الموارد البشرية الإقامة ورخصة العمل والتأمين.")} /></div>}
            <Card flush>
              {docs.length === 0 ? <EmptyState title={L("No documents on file", "لا توجد وثائق")} desc={L("HR adds your Iqama, passport and insurance here.", "تضيف الموارد البشرية الإقامة والجواز والتأمين هنا.")} /> : (
                <div className="tw">
                  <table className="tbl cards" data-testid="docs-table">
                    <thead><tr><th>{t("Document")}</th><th className="hide-md">{t("Number")}</th><th>{t("Expiry")}</th><th className="end">{t("Days left")}</th><th className="end">{t("Actions")}</th></tr></thead>
                    <tbody>
                      {docs.map((d) => {
                        const personal = isPersonalDoc(d.kind);
                        const n = daysTo(today, d.expiry);
                        return (
                          <Fragment key={d.kind}>
                            <tr data-testid={"doc-" + d.kind.replace(/\s+/g, "-")}>
                              <td className="k1"><span className="row nw"><Icon name={iconFor(d.kind)} className="s" />{t(d.kind)}</span><div className="xs muted">{personal ? L("You renew this", "تجدده بنفسك") : L("Renewed by your HR team", "تجدده الموارد البشرية")}</div></td>
                              <td className="hide-md num sm">{d.number}</td>
                              <td className="x" data-l={t("Expiry")}><DualDate date={d.expiry} /></td>
                              <td className="end k2"><ExpiryChip date={d.expiry} today={today} /></td>
                              <td className="end k3" data-l="">
                                {personal
                                  ? <Button variant={n <= 90 ? "sm" : "ghost sm"} icon="book" aria-expanded={guide === d.kind} onClick={() => setGuide(guide === d.kind ? null : d.kind)} data-testid={"btn-guide-" + d.kind.replace(/\s+/g, "-")}>{L("How to renew", "طريقة التجديد")}</Button>
                                  : requested[d.kind]
                                    ? <span className="chip info">{L("Requested", "تم الطلب")}</span>
                                    : <Button variant={n <= 90 ? "sm" : "ghost sm"} icon="mail" onClick={() => request(d)} data-testid={"btn-request-" + d.kind.replace(/\s+/g, "-")}>{L("Request renewal", "طلب التجديد")}</Button>}
                              </td>
                            </tr>
                            {(personal ? guide === d.kind : n <= 90) && (
                              <tr className="detail">
                                <td className="k1" colSpan={5} style={{ background: "var(--sunken)" }}>
                                  {personal
                                    ? <RenewalGuide kind={d.kind} audience="employee" compact />
                                    : <p className="sm" style={{ margin: 0 }} data-testid={"hr-note-" + d.kind.replace(/\s+/g, "-")}><Icon name="info" className="s" /> {L("Your HR team renews this — request renewal if you haven't heard from them.", "تجدد الموارد البشرية هذه الوثيقة — اطلب التجديد إن لم يتواصلوا معك.")}</p>}
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        );
      }}</Loaded>
    </div>
  );
}

export default function EssDocs() {
  const id = useEmployeeId();
  return id == null ? <NoLink /> : <Body empId={id} />;
}
